// The JS Irish-grammar engine: a faithful port of the Ruby `IrishGrammar` (app/services/irish_grammar.rb
// + irish_grammar/verb.rb). Same rules, same output — both sides are held to the shared golden corpus
// spec/fixtures/irish_grammar_cases.json (see irish_grammar.spec.ts). Having the rules here lets us
// generate forms client-side: the server sends only the verb metadata (conjugation class) + any stored
// overrides, and we generate the default paradigm for previews, the editor's placeholders, and a
// standalone tool — no per-record conjugation blob and no round-trip.
//
// See docs/reference/irish-grammar-rules.md for the documented ruleset. Scope matches Ruby: mutations are
// fully rule-based; verb conjugation is regular first-conjugation + an explicit irregular table (bí, is).
// Anything outside that (second conjugation, other irregulars) returns undefined so callers fall back to
// stored overrides rather than emitting wrong Irish.

const VOWELS = "aeiouáéíóú"
const LENITABLE = ["b", "c", "d", "f", "g", "m", "p", "t"]
const ECLIPSIS: Record<string, string> = {
  b: "m", c: "g", d: "n", f: "bh", g: "n", p: "b", t: "d",
}

const isVowel = (char: string): boolean => !!char && VOWELS.includes(char.toLowerCase())

// A word is lenitable if it starts with b/c/d/f/g/m/p/t, or s before a vowel or l/n/r.
const lenitable = (word: string): boolean => {
  const first = word[0].toLowerCase()
  if (first === "s") {
    const second = word[1]?.toLowerCase()
    return second == null || isVowel(second) || ["l", "n", "r"].includes(second)
  }
  return LENITABLE.includes(first)
}

/** Lenition (séimhiú): "cat" -> "chat". No change for l/n/r, vowels, or s in clusters (scoil -> scoil). */
export const lenite = (word: string): string => {
  if (!word) return word
  if (!lenitable(word)) return word
  return `${word[0]}h${word.slice(1)}`
}

/** Eclipsis (urú): "cat" -> "gcat", "áit" -> "n-áit". Prefix is lowercase; the word keeps its case. */
export const eclipse = (word: string): string => {
  if (!word) return word
  const first = word[0].toLowerCase()
  const prefix = ECLIPSIS[first]
  if (prefix) return `${prefix}${word}`
  if (isVowel(first)) return `n-${word}`
  return word
}

/** Vocative: the particle "a" + lenition, e.g. "Bríd" -> "a Bhríd". */
export const vocative = (name: string): string => (name ? `a ${lenite(name)}` : name)

// --- Reverse mutations (lemmatization / term frequency) -----------------------------------------------
// Irish mutates the START of a word, so recovering a root means stripping the mutation. Each returns null
// when the surface isn't in that mutated form, so callers gather candidates without guessing (see
// candidateRoots). Same rules as Ruby IrishGrammar; cases are in the shared golden corpus.

/** Reverse of lenite: "chat" -> "cat", "bhord" -> "bord", "shúil" -> "súil". null when not lenited. */
export const unlenite = (word: string): string | null => {
  if (!word || word.length < 2) return null
  const first = word[0].toLowerCase()
  if (!(LENITABLE.includes(first) || first === "s")) return null
  if (word[1].toLowerCase() !== "h") return null
  return `${word[0]}${word.slice(2)}`
}

/** Reverse of eclipse: "gcat" -> "cat", "bhfear" -> "fear", "nGaillimh" -> "Gaillimh", "n-áit" -> "áit". */
export const uneclipse = (word: string): string | null => {
  if (!word) return null
  const down = word.toLowerCase()
  if (down.startsWith("n-")) return word.slice(2)
  for (const [rootInitial, prefix] of Object.entries(ECLIPSIS)) {
    if (down.startsWith(`${prefix}${rootInitial}`)) return word.slice(prefix.length)
  }
  return null
}

/** Strip a t-/h- vowel buffer: "hÉireann" -> "Éireann", "t-arán" -> "arán", "tsúil" -> "súil". */
export const stripProthesis = (word: string): string | null => {
  if (!word || word.length < 2) return null
  const down = word.toLowerCase()
  if (down.startsWith("t-") || down.startsWith("h-")) return word.slice(2)
  if (down[0] === "h" && isVowel(down[1])) return word.slice(1)
  if (down[0] === "t" && down[1] === "s") return word.slice(1)
  return null
}

/**
 * Every plausible unmutated root for a surface token, surface first, for a resolver to test against a
 * lemma index. We generate candidates rather than guess one, because a surface form is ambiguous out of
 * context and Irish changes spelling at the start of words.
 */
export const candidateRoots = (word: string): string[] => {
  if (!word) return []
  const candidates = [word, uneclipse(word), unlenite(word), stripProthesis(word)]
  return [...new Set(candidates.filter((c): c is string => !!c))]
}

// --- Verb conjugation ---------------------------------------------------------------------------------

export const TENSES = ["present", "past", "future"] as const
export type Tense = (typeof TENSES)[number]
export const CONJ_PERSONS = ["i", "you", "he", "she", "we", "you_pl", "they"] as const
export type Person = (typeof CONJ_PERSONS)[number]

const PRONOUNS: Record<string, string> = { you: "tú", he: "sé", she: "sí", you_pl: "sibh", they: "siad" }

// Explicit forms for irregular verbs (mirrors IrishGrammar::Verb::IRREGULAR). `is` is the copula, so only
// its base particles are recorded.
const IRREGULAR: Record<string, Partial<Record<Tense, Partial<Record<Person, string>>>>> = {
  bí: {
    present: { i: "táim", you: "tá tú", he: "tá sé", she: "tá sí", we: "táimid", you_pl: "tá sibh", they: "tá siad" },
    past: { i: "bhí mé", you: "bhí tú", he: "bhí sé", she: "bhí sí", we: "bhíomar", you_pl: "bhí sibh", they: "bhí siad" },
    future: { i: "beidh mé", you: "beidh tú", he: "beidh sé", she: "beidh sí", we: "beimid", you_pl: "beidh sibh", they: "beidh siad" },
  },
  is: {
    present: { he: "is é", she: "is í", they: "is iad" },
    past: { he: "ba é", she: "ba í", they: "ba iad" },
  },
}

/** Broad if the last vowel of the stem is a/o/u (á/ó/ú); slender if e/i (é/í). */
const isBroad = (lemma: string): boolean => {
  const chars = lemma.toLowerCase().split("").reverse()
  const last = chars.find((c) => VOWELS.includes(c))
  return !!last && ["a", "o", "u", "á", "ó", "ú"].includes(last)
}

// First-conjugation present: synthetic 1sg/1pl, analytic elsewhere (ritheann sé).
const present = (lemma: string, person: Person, broad: boolean): string => {
  if (person === "i") return `${lemma}${broad ? "aim" : "im"}`
  if (person === "we") return `${lemma}${broad ? "aimid" : "imid"}`
  return `${lemma}${broad ? "ann" : "eann"} ${PRONOUNS[person]}`
}

// Lenite for the past: d' before a vowel or f, séimhiú otherwise (mhol, chodail, d'ól, d'oscail).
const pastStem = (word: string): string => {
  const stem = lenite(word)
  const first = word[0]?.toLowerCase() ?? ""
  return isVowel(first) || first === "f" ? `d'${stem}` : stem
}

// First-conjugation past: lenite the stem, analytic, synthetic 1pl.
const past = (lemma: string, person: Person, broad: boolean): string => {
  const stem = pastStem(lemma)
  if (person === "we") return `${stem}${broad ? "amar" : "eamar"}`
  const subject = person === "i" ? "mé" : PRONOUNS[person]
  return `${stem} ${subject}`
}

// First-conjugation future: -faidh/-fidh, synthetic 1pl (-faimid/-fimid).
const future = (lemma: string, person: Person, broad: boolean): string => {
  if (person === "we") return `${lemma}${broad ? "faimid" : "fimid"}`
  const subject = person === "i" ? "mé" : PRONOUNS[person]
  return `${lemma}${broad ? "faidh" : "fidh"} ${subject}`
}

// --- Second conjugation -------------------------------------------------------------------------------
// The stem the endings attach to: -aigh/-igh verbs drop the ending (ceannaigh -> ceann, éirigh -> éir);
// syncopating verbs drop the short vowel of a final `…C v+ [lnrs]` syllable (oscail -> oscl, labhair ->
// labhr). A long vowel or any other ending doesn't syncope (foghlaim keeps its stem).
const secondStem = (lemma: string): string => {
  const lower = lemma.toLowerCase()
  if (lower.endsWith("aigh")) return lemma.slice(0, -4)
  if (lower.endsWith("igh")) return lemma.slice(0, -3)
  const match = lemma.match(/^(.+[^aeiouáéíóú])([aeiou]+)([lnrs])$/i)
  return match ? `${match[1]}${match[3]}` : lemma
}

// Present: synthetic 1sg/1pl (ceannaím/ceannaímid), analytic elsewhere (ceannaíonn sé).
const presentSecond = (lemma: string, person: Person): string => {
  const stem = secondStem(lemma)
  const broad = isBroad(stem)
  if (person === "i") return `${stem}${broad ? "aím" : "ím"}`
  if (person === "we") return `${stem}${broad ? "aímid" : "ímid"}`
  return `${stem}${broad ? "aíonn" : "íonn"} ${PRONOUNS[person]}`
}

// Past: lenite the FULL lemma for the analytic forms (cheannaigh sé, d'oscail sé); the synthetic 1pl
// takes the syncopated stem + -aíomar/-íomar (cheannaíomar, d'osclaíomar).
const pastSecond = (lemma: string, person: Person): string => {
  const stem = secondStem(lemma)
  if (person === "we") return `${pastStem(stem)}${isBroad(stem) ? "aíomar" : "íomar"}`
  const subject = person === "i" ? "mé" : PRONOUNS[person]
  return `${pastStem(lemma)} ${subject}`
}

// Future: stem + -óidh/-eoidh, synthetic 1pl (-óimid/-eoimid).
const futureSecond = (lemma: string, person: Person): string => {
  const stem = secondStem(lemma)
  const broad = isBroad(stem)
  if (person === "we") return `${stem}${broad ? "óimid" : "eoimid"}`
  const subject = person === "i" ? "mé" : PRONOUNS[person]
  return `${stem}${broad ? "óidh" : "eoidh"} ${subject}`
}

/** Overrides, keyed dialect -> tense -> person (the shape stored in `conjugation_forms`). */
export type ConjugationForms = Record<string, Partial<Record<Tense, Partial<Record<Person, string>>>>>

export interface ConjugateOptions {
  conjugationClass?: string | null
  /** Stored corrections/variants; win over generation. Falls back standard when a dialect cell is empty. */
  forms?: ConjugationForms
  dialect?: string
}

/**
 * The single form for one tense+person. Resolution mirrors Ruby: stored override for the dialect ->
 * stored standard -> irregular table -> generated first-conjugation form. Returns undefined when it's
 * outside the covered slice (a stated non-first conjugation class that isn't a known irregular), so
 * callers show no placeholder rather than wrong Irish.
 */
export const conjugate = (
  lemma: string,
  tense: Tense,
  person: Person,
  opts: ConjugateOptions = {}
): string | undefined => {
  const dialect = opts.dialect ?? "standard"
  const stored = opts.forms?.[dialect]?.[tense]?.[person]
  if (stored) return stored
  if (dialect !== "standard") {
    const std = opts.forms?.standard?.[tense]?.[person]
    if (std) return std
  }

  const trimmed = (lemma || "").trim()
  const key = trimmed.toLowerCase()
  if (IRREGULAR[key]) return IRREGULAR[key][tense]?.[person]
  if (!trimmed) return undefined

  // Second conjugation (regular slice: -igh/-aigh + short-vowel syncope). Mirrors IrishGrammar::Verb.
  if (opts.conjugationClass === "second") {
    if (tense === "present") return presentSecond(trimmed, person)
    if (tense === "past") return pastSecond(trimmed, person)
    if (tense === "future") return futureSecond(trimmed, person)
    return undefined
  }
  // First conjugation (unspecified class defaults to first); any other class defers to overrides so we
  // never emit a wrong guess.
  if (opts.conjugationClass && opts.conjugationClass !== "first") return undefined

  const broad = isBroad(trimmed)
  if (tense === "present") return present(trimmed, person, broad)
  if (tense === "past") return past(trimmed, person, broad)
  if (tense === "future") return future(trimmed, person, broad)
  return undefined
}

/** The full paradigm for a tense (person -> form), dropping any person the engine can't produce. */
export const paradigm = (
  lemma: string,
  tense: Tense,
  opts: ConjugateOptions = {}
): Partial<Record<Person, string>> => {
  const out: Partial<Record<Person, string>> = {}
  for (const person of CONJ_PERSONS) {
    const form = conjugate(lemma, tense, person, opts)
    if (form) out[person] = form
  }
  return out
}
